import { useApi } from "@appretio/api";
import { ContextSelectedOptions } from "@appretio/context-selector";
import { useQuery } from "@tanstack/react-query";
import { TemporalContext } from "../../../components/temporalContextSelector/data/temporalContextOptions";
import { convertOptionsToApiFormat } from "../utils/convertToApiFormat";
import { priceDilutionQueryKeys } from "./queryKeys";

export type PriceDilutionApiRow = {
  style_recommended_retail_price_eur: number;
  /**
   *  Gross Sales 1 - Sum of sales value
   */
  gs1_sum: number;
  /**
   * Gross Sales 2 - Sum of sales value
   */
  gs2_sum: number;
  /**
   * Gross Intake Sales 1 - Sum of intake sales value
   */
  gis1_sum: number;
  /**
   * Net Sales 3 - Sum of net sales value
   */
  ns3_sum: number;
  /**
   * Net Intake Sales 3 - Sum of net intake sales value
   */
  nis3_sum: number;
  /**
   * Gross Returns 2 - Sum of returns value
   */
  gr2_sum: number;
  /**
   * Gross Intake Returns - Sum of intake returns value
   */
  gir_sum: number;
  /**
   * Time Purchase - Sum of purchase timing values
   */
  time_purchase_sum: number;
  /**
   * Assortment Size - Count of unique price points
   */
  assortment_size: number;
};

export function useFetchPriceDilution(
  contextOptions: ContextSelectedOptions,
  temporalOptions: TemporalContext,
  enabled: boolean = false
) {
  const apiClient = useApi();

  return useQuery({
    queryKey: priceDilutionQueryKeys.data({ contextOptions, temporalOptions }),
    queryFn: async () => {
      const options = convertOptionsToApiFormat(
        contextOptions,
        temporalOptions
      );

      try {
        const response = await apiClient.POST(
          "/v1/in-season/review/get-review-data",
          {
            body: {
              ...options,
              query_params: {
                offset: 0,
                group: {
                  columns: ["style_recommended_retail_price_eur"]
                },
                aggregates: [
                  { column: "gs1", function: "sum", alias: "gs1_sum" },
                  { column: "gs2", function: "sum", alias: "gs2_sum" },
                  { column: "gis1", function: "sum", alias: "gis1_sum" },
                  { column: "ns3", function: "sum", alias: "ns3_sum" },
                  { column: "nis3", function: "sum", alias: "nis3_sum" },
                  { column: "gr2", function: "sum", alias: "gr2_sum" },
                  { column: "gir", function: "sum", alias: "gir_sum" },
                  {
                    column: "time_purchase",
                    function: "sum",
                    alias: "time_purchase_sum"
                  },
                  {
                    column: "style_recommended_retail_price_eur",
                    function: "count",
                    alias: "assortment_size"
                  }
                ]
              }
            }
          }
        );

        return (response.data?.result as PriceDilutionApiRow[]) ?? [];
      } catch (error) {
        console.error("Error fetching context options:", error);

        throw error; // Let TanStack Query handle the error state
      }
    },
    enabled, // Only fetch when enabled is true
    staleTime: 5 * 60 * 1000 // 5 minutes
  });
}
